import { useGetAllEmployeeKPIByAdminQuery } from "@/app/features/employee/performance/performance.api";
import useAuth from "@/hooks/useAuth";
import useDiscloser from "@/hooks/useDiscloser";
import { EyeIcon } from "@/icons";
import { formatDate, normalizeColumnValue } from "@/utils";
import { Button, Space, type TableProps } from "antd";
import CustomTable from "../../../../components/CustomTable";
import StatusTag from "../../../../components/StatusTag";
import { FILTER_KEYS } from "../../../../constants/keys.constants";
import { useAppFilters } from "../../../../hooks";
import { countParPageSerialNumber } from "../../../../utils/global.utils";
import ViewOwnKPI from "../../../Performance/_components/ViewOwnKPI";

const STATUS_CONFIG = [
  {
    label: "Not Started",
    value: "NOT_STARTED",
    className: "status status-slate",
  },
  {
    label: "KPI Set-Up Start",
    value: "PENDING",
    className: "status status-fuchsia",
  },
  {
    label: "Approved",
    value: "APPROVED",
    className: "status status-success",
  },
  {
    label: "Submitted",
    value: "SUBMITTED",
    className: "status status-sky",
  },
  {
    label: "Rejected",
    value: "REJECTED",
    className: "status status-error",
  },
];

export default function KPISetUPReport() {
  const { organizationId } = useAuth();
  const { page, limit, handlePagination, filters } = useAppFilters(
    FILTER_KEYS.PERFORMANCE_KPI_REPORT,
  );
  const [config, handleOpen, handleClose] = useDiscloser<"VIEW">();

  const { data, isFetching } = useGetAllEmployeeKPIByAdminQuery({
    organization_id: organizationId,
    page,
    limit,
    ...filters,
  });

  const kpiData = data?.data?.employees || [];
  const total = data?.data?.pagination?.total || 0;

  const columns: TableProps<any>["columns"] = [
    {
      title: "SL NO.",
      align: "center",
      width: 80,
      render: (_, __, index) => countParPageSerialNumber(page, limit, index),
    },
    {
      title: "Employee ID",
      dataIndex: ["employee", "employee_id"],
      render: (val: string) => normalizeColumnValue(val, "N/A", false),
    },
    {
      title: "Employee Name",
      dataIndex: ["employee", "name"],
      render: (val: string) => normalizeColumnValue(val, "N/A", false),
    },
    {
      title: "Designation",
      dataIndex: ["employee", "designation"],
      render: (val: string) => normalizeColumnValue(val, "N/A", false),
    },
    {
      title: "Department",
      dataIndex: ["employee", "department"],
      render: (val: string) => normalizeColumnValue(val, "N/A", false),
    },
    {
      title: "Line Manager",
      dataIndex: ["employee", "manager"],
      render: (val: string) => normalizeColumnValue(val, "N/A", false),
    },
    {
      title: "Plan Name",
      dataIndex: "plan_name",
      render: (val: string) => normalizeColumnValue(val, "N/A", false),
    },
    {
      title: "Due Date",
      dataIndex: "kpi_due_date",
      align: "center",
      render: (val: string) => (val ? formatDate(val) : "N/A"),
    },
    {
      title: "Status",
      dataIndex: "kpi_status",
      align: "center",
      render: (val: string) => (
        <StatusTag
          value={val}
          config={STATUS_CONFIG}
          shouldCapitalize={false}
        />
      ),
    },
    {
      title: "Actions",
      dataIndex: "mapping_id",
      align: "end",
      render: (id: string, record: any) => (
        <Space size={8}>
          <Button
            size="small"
            className="action-btn"
            disabled={record.kpi_status === "PENDING"}
            icon={<EyeIcon size={20} />}
            onClick={() =>
              handleOpen({
                type: "VIEW",
                data: {
                  mappingId: id,
                  planName: record.plan_name,
                },
              })
            }
          />
        </Space>
      ),
    },
  ];

  return (
    <div>
      <CustomTable
        page={page}
        limit={limit}
        hideRowSelect
        columns={columns}
        dataSource={kpiData}
        onPaginate={handlePagination}
        customTitle="KPI Set-Up List"
        loading={isFetching}
        total={total}
        filterSection={<></>}
      />

      <ViewOwnKPI
        open={config.open && config.type === "VIEW"}
        onClose={handleClose}
        mappingId={config.data?.mappingId}
        planName={config.data?.planName}
        source="TEAM"
      />
    </div>
  );
}
